// The student answer slot (a MathLive prompt, PBI 538086) is drawn inside the field's shadow DOM, and
// mathlive 0.106 renders it without the `part="prompt"` its source declares, so `::part()` cannot
// reach it from a stylesheet. The rule is adopted into the shadow root instead; its colours are
// custom properties, which do cross the shadow boundary, so MathLiveField.css themes it per field.
//
// A slot is framed like every other Cloze blank: a thin solid line with rounded corners, thicker while it
// has the caret. A graded slot is framed in the theme's success / failure colour and carries its own small
// ✓/✗ mark (the Cloze blank icon's glyphs), never mathlive's own mark or glow; a slotted blank shows no
// icon of its own. The mark sits top-right in either content direction: the math inside the field is
// always LTR, and on the left it would cover the root sign of a slot under a root.
const CHECK_GLYPH = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 28 29'%3E%3Cpath d='M23.3332 7.5L10.4998 21.5L4.6665 15.1364' fill='none' stroke='white' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`;
const CROSS_GLYPH = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 33'%3E%3Cpath d='M8 8.5L24 24.5M24 8.5L8 24.5' fill='none' stroke='white' stroke-width='4' stroke-linecap='round'/%3E%3C/svg%3E")`;

const ANSWER_SLOT_CSS = `
/* Every frame is drawn INSIDE MathLive's slot (negative offset): an outline otherwise grows past the slot
 * it frames, and in a fraction that reaches the bar. */
.ML__editablePromptBox {
  outline: 1px solid var(--mlf-answer-slot-color) !important;
  outline-offset: -1px;
  border-radius: 4px;
  background: var(--mlf-answer-slot-background);
}
/* MathLive draws a slot's frame too high around its content, so the value sits at the frame's bottom
 * edge. Every slot has a digit's metrics (an empty one holds an invisible digit — see
 * EMPTY_MATH_SLOT_FILL), so one shift centres them all; measured against MathLive 0.106, in em so it
 * scales with the field. */
.ML__prompt {
  transform: translateY(0.2em);
}
.ML__focused .ML__focusedPromptBox {
  outline-width: 2px !important;
  outline-offset: -2px;
}
/* The author's slots carry the number of their answer field (numberSlots). */
.ML__prompt[data-slot-number]::after {
  content: attr(data-slot-number);
  position: absolute;
  /* px, not em: the field's font is large, and the badge must stay a small, fixed-size mark. */
  top: -9px;
  left: -9px;
  box-sizing: border-slot;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--mlf-answer-slot-color);
  color: #fff;
  font: 600 11px/16px system-ui, sans-serif;
  text-align: center;
  z-index: 1;
}
.ML__correctPromptBox,
.ML__incorrectPromptBox {
  box-shadow: none !important;
}
.ML__correctPromptBox {
  outline-color: var(--mantine-color-success-0, #2f9e44) !important;
}
.ML__incorrectPromptBox {
  outline-color: var(--mantine-color-failure-0, #e03131) !important;
}
.ML__correctPromptBox::before,
.ML__incorrectPromptBox::before {
  content: '';
  position: absolute;
  top: -7px;
  right: -6px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: center / 8px no-repeat;
  z-index: 1;
}
.ML__correctPromptBox::before {
  background-color: var(--mantine-color-success-0, #2f9e44);
  background-image: ${CHECK_GLYPH};
}
.ML__incorrectPromptBox::before {
  background-color: var(--mantine-color-failure-0, #e03131);
  background-image: ${CROSS_GLYPH};
}
.ML__prompt-atom svg {
  display: none;
}
/* The slots are the inputs of a slot-expression field, so only they show the text cursor. Their size is
 * MathLive's: it lays out fraction bars and scripts from its own measure of each slot, so a CSS size on
 * the slot pulls it out of line with the rest of the formula. */
:host(.mlf-slots:not([disabled])) .ML__prompt-atom {
  cursor: text;
}
`;

let sheet: CSSStyleSheet | null | undefined;

function answerSlotSheet(): CSSStyleSheet | null {
  if (sheet === undefined) {
    try {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(ANSWER_SLOT_CSS);
    } catch {
      // No constructable stylesheets (jsdom, very old engines): the slot keeps mathlive's grey outline.
      sheet = null;
    }
  }
  return sheet;
}

export function adoptAnswerSlotStyles(element: HTMLElement) {
  const root = element.shadowRoot;
  const slotSheet = answerSlotSheet();
  if (!root || !slotSheet || !Array.isArray(root.adoptedStyleSheets)) return;
  if (root.adoptedStyleSheets.includes(slotSheet)) return;
  root.adoptedStyleSheets = [...root.adoptedStyleSheets, slotSheet];
}
